import { useState, useEffect, useCallback } from "react";
import { RenderMarkdownWithLatex } from "../../utils/utils";

const apiUrl = process.env.REACT_APP_API_SOURCE;
const token = () => localStorage.getItem("token");

const STATUS_COLORS = {
  "1_problem_generated":  "bg-gray-100 text-gray-700",
  "2_problem_verified":   "bg-blue-100 text-blue-700",
  "3_solution_generated": "bg-yellow-100 text-yellow-700",
  "4_solution_verified":  "bg-purple-100 text-purple-700",
  "5_approved":           "bg-green-100 text-green-700",
  "6_migrated":           "bg-emerald-100 text-emerald-700",
  "99_rejected":          "bg-red-100 text-red-700",
};

const Section = ({ label, children, color = "blue" }) => {
  const borders = { blue: "border-blue-200", green: "border-green-200", amber: "border-amber-200", gray: "border-gray-200", red: "border-red-200" };
  const bgs     = { blue: "bg-blue-50/30",   green: "bg-green-50/30",   amber: "bg-amber-50/20",  gray: "bg-gray-50",     red: "bg-red-50/30" };
  return (
    <div className={`mt-3 border ${borders[color]} rounded-md overflow-hidden`}>
      <div className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 border-b ${borders[color]} ${bgs[color]}`}>
        {label}
      </div>
      <div className={`px-3 py-2 ${bgs[color]} text-sm text-gray-800 leading-relaxed`}>
        {children}
      </div>
    </div>
  );
};

const StagingCard = ({ item, index, total, statusLabels, onDelete }) => {
  const [currentStatus, setCurrentStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);

  const isApproved = currentStatus === "5_approved";

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(item, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staging_${item.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Usunąć zadanie #${item.id} z bazy? Tej operacji nie można cofnąć.`)) return;
    try {
      const res = await fetch(`${apiUrl}ai-problem-staging/${item.id}/delete/`, {
        method: "DELETE",
        headers: { Authorization: `Token ${token()}` },
      });
      if (!res.ok) throw new Error();
      onDelete(item.id);
    } catch {
      alert("Błąd usuwania.");
    }
  };

  const handleToggleApprove = async () => {
    const newStatus = isApproved ? "1_problem_generated" : "5_approved";
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}ai-problem-staging/${item.id}/set-status/`, {
        method: "POST",
        headers: { Authorization: `Token ${token()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      setCurrentStatus(newStatus);
    } catch {
      alert("Błąd zapisu statusu.");
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = statusLabels[currentStatus] || currentStatus;
  const statusColor = STATUS_COLORS[currentStatus] || "bg-gray-100 text-gray-700";
  const isRejected = currentStatus === "99_rejected";

  return (
    <div className={`border rounded-lg p-4 sm:p-5 mb-5 shadow-sm bg-white ${isRejected ? "border-red-300" : isApproved ? "border-green-300" : "border-gray-200"}`}>
      {/* Header */}
      <div className="mb-3 pb-2 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-sm font-bold text-gray-500">#{item.id}</span>
          <span className="text-xs text-gray-400">{index + 1} z {total}</span>
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
          {item.is_original_cke && <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700">CKE oryginał</span>}
          {item.is_open
            ? <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">Otwarte</span>
            : <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">Zamknięte</span>
          }
          {item.points_assigned != null && (
            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600">{item.points_assigned} pkt</span>
          )}
        </div>

        <div className="flex flex-wrap gap-x-3 text-xs mt-1">
          {item.field_name && <span className="font-semibold text-indigo-600">{item.field_name}</span>}
          {item.subfield_name && <span className="text-gray-500">› {item.subfield_name}</span>}
          {item.template_name && <span className="text-gray-400 italic">› {item.template_name}</span>}
        </div>

        <div className="flex flex-wrap gap-x-4 text-xs text-gray-400 mt-0.5">
          <span>{new Date(item.created_at).toLocaleString("pl-PL")}</span>
          {item.target_template_id && <span>template_id: {item.target_template_id}</span>}
          {item.source_matura_problem_ids?.length > 0 && (
            <span>CKE źródła: [{item.source_matura_problem_ids.join(", ")}]</span>
          )}
          {item.target_matura_formula_ids?.length > 0 && (
            <span>Wzory: [{item.target_matura_formula_ids.join(", ")}]</span>
          )}
        </div>

        <div className="flex gap-2 mt-2">
          <button
            onClick={handleDownload}
            className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Pobierz treść (JSON)
          </button>
          <button
            onClick={handleDelete}
            className="text-xs px-3 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
          >
            Usuń
          </button>
          <button
            onClick={handleToggleApprove}
            disabled={saving}
            className={`text-xs px-3 py-1 rounded font-semibold disabled:opacity-50 ${
              isApproved
                ? "border border-green-400 bg-green-50 text-green-700 hover:bg-green-100"
                : "border border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100"
            }`}
          >
            {saving ? "..." : isApproved ? "✓ Zatwierdzone — cofnij" : "Zatwierdź"}
          </button>
        </div>
      </div>

      {item.raw_question && (
        <Section label="Treść zadania" color="gray">
          <RenderMarkdownWithLatex text={item.raw_question} />
        </Section>
      )}

      {item.raw_true_answer && (
        <Section label="Odpowiedź poprawna" color="green">
          <RenderMarkdownWithLatex text={item.raw_true_answer} />
        </Section>
      )}

      {(item.raw_false_answer1 || item.raw_false_answer2 || item.raw_false_answer3) && (
        <Section label="Dystractory" color="gray">
          {[item.raw_false_answer1, item.raw_false_answer2, item.raw_false_answer3]
            .filter(Boolean)
            .map((a, i) => (
              <div key={i} className="flex items-start gap-1 mb-1">
                <span className="text-gray-400 font-mono text-xs shrink-0 mt-0.5">F{i + 1}:</span>
                <RenderMarkdownWithLatex text={a} />
              </div>
            ))}
        </Section>
      )}

      {item.raw_hint && (
        <Section label="Podpowiedź" color="amber">
          <RenderMarkdownWithLatex text={item.raw_hint} />
        </Section>
      )}

      {item.raw_solution && (
        <Section label="Rozwiązanie wzorcowe" color="blue">
          <RenderMarkdownWithLatex text={item.raw_solution} />
        </Section>
      )}

      {item.grading_criteria && (
        <Section label="Kryteria oceniania" color="green">
          <RenderMarkdownWithLatex text={item.grading_criteria} />
        </Section>
      )}

      {item.formulas_detail?.length > 0 && (
        <Section label={`Wzory z karty (${item.formulas_detail.length})`} color="amber">
          <div className="space-y-2">
            {item.formulas_detail.map((f) => (
              <div key={f.id} className="flex items-start gap-2 text-xs">
                <span className="shrink-0 font-mono text-gray-400">#{f.id}</span>
                <div>
                  <span className="font-semibold text-gray-700">{f.name}</span>
                  <span className="text-gray-400 ml-2 italic">{f.domain}</span>
                  <div className="mt-0.5">
                    <RenderMarkdownWithLatex text={`${f.text}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {item.matura_problems_detail?.length > 0 && (
        <Section label={`Zadania CKE źródłowe (${item.matura_problems_detail.length})`} color="gray">
          <div className="space-y-3">
            {item.matura_problems_detail.map((p) => (
              <div key={p.id} className="text-xs border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-gray-400">#{p.id}</span>
                  <span className="text-gray-500">{p.source}</span>
                  <span className="font-semibold text-gray-600">{p.points} pkt</span>
                  {p.is_open
                    ? <span className="text-amber-600 font-medium">Otwarte</span>
                    : <span className="text-blue-600 font-medium">Zamknięte</span>
                  }
                </div>
                <RenderMarkdownWithLatex text={p.question} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {item.error_log && (
        <Section label="Error log" color="red">
          <pre className="text-xs whitespace-pre-wrap text-red-700">{item.error_log}</pre>
        </Section>
      )}
    </div>
  );
};

const AIStagingAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);

  const [filterOptions, setFilterOptions] = useState({ field_names: [], subfield_names: [], template_names: [], statuses: [] });
  const [filters, setFilters] = useState({ status: "", field_name: "", subfield_name: "", template_name: "" });
  const [statusLabels, setStatusLabels] = useState({});

  useEffect(() => {
    fetch(`${apiUrl}ai-problem-staging/filter-options/`, {
      headers: { Authorization: `Token ${token()}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setFilterOptions(data);
        const labels = {};
        (data.statuses || []).forEach(({ value, label }) => { labels[value] = label; });
        setStatusLabels(labels);
      })
      .catch(() => {});
  }, []);

  const fetchItems = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (f.status) params.set("status", f.status);
      if (f.field_name) params.set("field_name", f.field_name);
      if (f.subfield_name) params.set("subfield_name", f.subfield_name);
      if (f.template_name) params.set("template_name", f.template_name);
      const res = await fetch(`${apiUrl}ai-problem-staging/?${params}`, {
        headers: { Authorization: `Token ${token()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.results ?? [];
      setItems(list);
      setCount(list.length);
    } catch {
      setError("Nie udało się pobrać danych.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFetch = () => {
    fetchItems(filters);
  };

  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  const sel = "border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]";

  return (
    <div className="container mx-auto px-4 py-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-5 text-gray-800 border-b pb-2">AI Problem Staging</h1>

      <div className="bg-white p-4 rounded-lg shadow mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Status</label>
            <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className={sel}>
              <option value="">Wszystkie</option>
              {filterOptions.statuses.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Kategoria</label>
            <select value={filters.field_name} onChange={(e) => setFilter("field_name", e.target.value)} className={sel}>
              <option value="">Wszystkie</option>
              {filterOptions.field_names.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Podkategoria</label>
            <select value={filters.subfield_name} onChange={(e) => setFilter("subfield_name", e.target.value)} className={sel}>
              <option value="">Wszystkie</option>
              {filterOptions.subfield_names.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Szablon</label>
            <select value={filters.template_name} onChange={(e) => setFilter("template_name", e.target.value)} className={sel}>
              <option value="">Wszystkie</option>
              {filterOptions.template_names.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <button
            onClick={handleFetch}
            disabled={loading}
            className="bg-blue-600 text-white px-5 py-2 rounded font-semibold hover:bg-blue-700 disabled:opacity-50 text-sm self-end"
          >
            {loading ? "Ładowanie..." : "Pobierz"}
          </button>
        </div>
      </div>

      {error && <p className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">{error}</p>}

      {!loading && items.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
          Wyświetlono <span className="font-bold">{items.length}</span> rekordów
        </div>
      )}

      {loading && <p className="text-blue-600 text-center py-6">Ładowanie...</p>}

      {!loading && items.map((item, i) => (
        <StagingCard
          key={item.id}
          item={item}
          index={i}
          total={count}
          statusLabels={statusLabels}
          onDelete={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
        />
      ))}

      {!loading && items.length === 0 && !error && (
        <p className="text-gray-500 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
          Wybierz filtry i kliknij „Pobierz".
        </p>
      )}
    </div>
  );
};

export default AIStagingAdminPage;
